import { deepClone } from '../../../deep_clone'
import { ensureEngineWasmSession } from '../../engine/engineWasm'
import { decodeStateSnapshotUnchecked, encodeActionMessage } from '../../engine/engineWasmCodec'
import { getEngineWasmCallErrorMessage, getEngineWasmSnapshotErrorMessage } from '../../engine/engineWasmErrors'
import type { Engine } from '../../engine/Engine.type'
import { setEngineStateFromExternal, setEngineStateFromWasm } from '../../engine/setEngineState'
import { StateMapDisk } from '../../state/state_map_disk.enum'
import type { State } from '../State.type'
import type Action from './action/Action.type'

export type ApplyActionWithWasmOptions = {
  rollbackState?: State
}

export default async function applyActionWithWasm(
  engine: Engine,
  action: Action,
  options: ApplyActionWithWasmOptions = {}
): Promise<State> {
  const rollbackState = deepClone(options.rollbackState ?? engine.state)

  try {
    const session = await ensureEngineWasmSession(engine)
    const actionResult = session.instance.applyAction(session.handle, encodeActionMessage(action))
    const actionError = getEngineWasmCallErrorMessage(actionResult)
    if (actionError) {
      throw new Error(actionError)
    }
    const snapshot = session.instance.getFullState(session.handle)
    const snapshotError = getEngineWasmSnapshotErrorMessage(snapshot)
    if (snapshotError) {
      throw new Error(snapshotError)
    }

    const preferredLocalDisk = engine.state.map.disk === StateMapDisk.Memory
      ? StateMapDisk.Memory
      : StateMapDisk.IndexedDB
    const nextState = decodeStateSnapshotUnchecked(snapshot, preferredLocalDisk)

    setEngineStateFromWasm(engine, nextState)
    return nextState
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error)
    console.error('[applyActionWithWasm] Action Failed:', message, (action as Action).type)
    await setEngineStateFromExternal(engine, rollbackState)
    throw toActionFailedError(message)
  }
}

function toActionFailedError(message: string): Error {
  const legacyMappings: Record<string, string> = {
    'OutOfAmmo': '!unitWeaponEstimate',
    'AboveMaxRange': '!unitWeaponEstimate',
    'BelowMinRange': '!unitWeaponEstimate',
    'DestinationOccupied': '!destOccupied',
    'BlankMove': '!moveWithNoEffect',
    'unitDefaultMobility!==unit.mobility': '!unitWeaponEstimate',
  }

  for (const [key, value] of Object.entries(legacyMappings)) {
    if (message.includes(key)) {
      return new Error(`${value} (${key})`)
    }
  }

  if (message.startsWith('Action Failed: ')) {
    return new Error(message)
  }

  return new Error(`Action Failed: ${message}`)
}
